import React, { useMemo } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { Train } from '@material-ui/icons';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Table, TableBody, TableRow, TableCell } from '@this/shared/ui/data_displays/table';
import CancelPolicySection from './cancel_policy_section';
import SameDayCancelRuleTable from './same_day_cancel_rule_table';

const RailwayCancelGuide: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 768px)', { noSsr: true });
  const styles = useMemo(() => getStyles(isMobile), [isMobile]);

  return (
    <CancelPolicySection
      title="新幹線・特急・在来線のキャンセルについて"
      icon={<Train style={{ fontSize: 18, color: '#fff' }} />}
    >
      <div style={styles.subsection}>
        <h3 style={styles.subsectionHeading}>キャンセル依頼期限について</h3>
        <SameDayCancelRuleTable />
      </div>

      <div style={styles.subsection}>
        <h3 style={styles.subsectionHeading}>キャンセル方法</h3>
        <Alert variant="warning" style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 13, lineHeight: 1.6 }}>
            紙のチケット受け取り後は、当社でのキャンセル操作ができません。
            お客様ご自身で、駅窓口でのお手続きをお願いいたします。
          </span>
        </Alert>
        <Table style={styles.table}>
          <TableBody style={styles.body}>
            <TableRow style={styles.row}>
              <TableCell th style={styles.labelCell}>
                紙のチケット受け取り前
              </TableCell>
              <TableCell style={styles.valueCell}>
                <p style={styles.methodLead}>当社オペレーターがキャンセル手続きを行います。</p>
                <p style={styles.methodText}>キャンセル依頼ボタンよりキャンセル依頼をお申し込みください。</p>
              </TableCell>
            </TableRow>
            <TableRow style={styles.row}>
              <TableCell th style={styles.labelCell}>
                紙のチケット受け取り後
              </TableCell>
              <TableCell style={styles.valueCell}>
                <p style={styles.methodLead}>お客様ご自身での対応が必要です。</p>
                <ol style={styles.methodList}>
                  <li style={styles.methodListItem}>
                    <span style={styles.methodListNum}>01</span>
                    <span>
                      列車出発時刻までに駅窓口（みどりの窓口）にて
                      <span style={{ fontWeight: 'bold' }}>「払戻証明をお願いします」</span>
                      とお伝えください
                    </span>
                  </li>
                  <li style={{ ...styles.methodListItem, marginBottom: 0 }}>
                    <span style={styles.methodListNum}>02</span>
                    <span>
                      <span style={{ fontWeight: 'bold' }}>「払戻申出」</span>
                      の印字があるチケットを、旅程番号を記載のうえ弊社宛に郵送してください
                    </span>
                  </li>
                </ol>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </CancelPolicySection>
  );
};

const getStyles = (isMobile: boolean) => {
  const cell: React.CSSProperties = {
    border: '1px solid #d3d7db',
    padding: '8px 12px',
    verticalAlign: 'top',
    fontSize: 13,
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    boxSizing: 'border-box',
    ...(isMobile ? { display: 'block', width: '100%' } : {})
  };
  return {
    subsection: {
      marginBottom: 16
    },
    subsectionHeading: {
      margin: '0 0 16px',
      padding: '10px 16px',
      background: '#f4f5f6',
      borderLeft: '4px solid #292929',
      fontSize: 14,
      fontWeight: 'bold' as const,
      lineHeight: 1.4
    },
    table: {
      borderCollapse: 'collapse' as const,
      width: '100%',
      fontSize: 13,
      ...(isMobile ? { display: 'block' } : {})
    },
    body: isMobile ? { display: 'block' as const } : undefined,
    row: isMobile ? { display: 'block' as const } : undefined,
    labelCell: {
      ...cell,
      backgroundColor: '#f4f5f6',
      fontWeight: 'bold' as const,
      ...(isMobile ? {} : { width: 120, minWidth: 120, maxWidth: 120 })
    },
    valueCell: {
      ...cell,
      backgroundColor: 'transparent'
    },
    methodLead: {
      fontWeight: 'bold' as const,
      fontSize: 13,
      margin: '0 0 8px'
    },
    methodText: {
      fontSize: 13,
      margin: 0
    },
    methodList: {
      listStyle: 'none' as const,
      padding: 0,
      margin: 0
    },
    methodListItem: {
      display: 'flex' as const,
      gap: 8,
      marginBottom: 8
    },
    methodListNum: {
      flexShrink: 0,
      fontWeight: 'bold' as const
    }
  };
};

export default RailwayCancelGuide;
